import {
  Box,
  Container,
  Grid,
  Group,
  Paper,
  Text,
  ThemeIcon,
  Title,
  UnstyledButton,
  useMantineTheme
} from '@mantine/core'
import {
  IconBuildingStore,
  IconCreditCard,
  IconHeadset,
  IconHome,
  IconReceipt,
  IconUser,
  IconUsers
} from '@tabler/icons'
import Link from 'next/link'

import { useAuth } from '@/providers/AuthProvider'
import { Site as SiteType } from '@/types'

interface Props {
  initialData?: {
    site: SiteType
  }
}

export default function AccountsView({ initialData }: Props) {
  // Hooks
  const theme = useMantineTheme()
  const { userData } = useAuth() || {}
  const { user } = userData || {}

  // Constants
  const iconSize = 30

  const pages = [
    {
      uid: 'stores',
      label: 'Comprar Agora',
      description: 'Acessar lojas para comprar produtos e serviços.',
      url: '/accounts/profiles/',
      icon: <IconBuildingStore size={iconSize} />,
      active: true
    },
    {
      uid: 'enrollments',
      label: 'Meus Alunos',
      description: 'Veja os detalhes das matrículas dos alunos.',
      url: '/accounts/enrollments/',
      icon: <IconUsers size={iconSize} />,
      active: initialData?.site?.hasSchoolManagementConfiguration || user?.isStaff,
      isStaff: user?.isStaff
    },
    {
      uid: 'profile',
      label: 'Meus Dados',
      description: 'Altere e mantenha seus dados atualizados.',
      url: '/accounts/profile/',
      icon: <IconUser size={iconSize} />,
      active: true
    },
    {
      uid: 'orders',
      label: 'Meus Pedidos',
      description: 'Acesse notas fiscais, pagamentos, cancelamento e devolução.',
      url: '/accounts/orders/',
      icon: <IconReceipt size={iconSize} />,
      active: true
    },
    {
      uid: 'help',
      label: 'Central de Ajuda',
      description: 'Acesse tópicos de ajuda, consulte canais de atendimento.',
      url: '/help/',
      icon: <IconHeadset size={iconSize} />,
      active: true
    },
    {
      uid: 'addresses',
      label: 'Meus Endereços',
      description: 'Gerencie seus endereços de entrega/cobrança',
      url: '/accounts/addresses/',
      icon: <IconHome size={iconSize} />,
      active: true
    },
    {
      uid: 'creditcards',
      label: 'Meus Cartões de Crédito',
      description: 'Gerencie seus cartões de crédito',
      url: '/accounts/credit-cards/',
      icon: <IconCreditCard size={iconSize} />,
      active: true
    }
  ]

  return (
    <Container px="xs" py="lg" size="xl">
      <Title mb="md">Minha Conta</Title>

      <Grid gutter="sm" align="stretch">
        {pages
          .filter(page => page.active)
          .map(page => (
            <Grid.Col key={page.uid} span={12} xs={6} sm={4} md={3}>
              <UnstyledButton component={Link} href={page.url}>
                <Paper
                  p="lg"
                  withBorder
                  sx={{
                    height: '100%',
                    ':hover': { backgroundColor: '#F5F5F5' },
                    backgroundColor: page.isStaff ? '#fd7e1411' : 'transparent',
                    borderColor: page.isStaff ? '#fd7e14' : '#dee2e6'
                  }}>
                  <Group position="left" spacing="sm" noWrap sx={{ height: '100%' }}>
                    <ThemeIcon
                      color={
                        page.isStaff ? theme.colors.orange[6] : theme.colors[theme.primaryColor][7]
                      }
                      size={50}
                      radius="xl">
                      {page.icon}
                    </ThemeIcon>
                    <Box>
                      <Title order={4} mb={0}>
                        {page.label}
                      </Title>
                      {page.description && (
                        <Text color="dimmed" size="sm">
                          {page.description}
                        </Text>
                      )}
                    </Box>
                  </Group>
                </Paper>
              </UnstyledButton>
            </Grid.Col>
          ))}
      </Grid>
    </Container>
  )
}
